import { createHttpClient } from '@adapters/HttpClient';
import { TeamsApiAdapter } from '@modules/Products/List/adapters/TeamsApiAdapter';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

/** Estado local de filtros para ubicaciones */
export interface LocationsFiltersState {
  /** Tipo de ubicación: '' = todos, '1' = almacén, '2' = picking */
  locationType: string;
  /** ID del producto (string) para filtrar por producto */
  productId: string;
  /** IDs de teams propietarios del producto */
  productTeamIds: string[];
}

const defaultState: LocationsFiltersState = {
  locationType: '',
  productId: '',
  productTeamIds: [],
};

/**
 * Parsea condiciones iniciales (desde URL) al estado local del panel.
 */
function parseConditions(conditions: FilterConditionOutput[]): LocationsFiltersState {
  const state = { ...defaultState };

  for (const c of conditions) {
    if (c.column === 'locationType' && c.action === 'equals' && c.value) {
      state.locationType = String(c.value);
    } else if (c.column === 'productName' && c.action === 'equals' && c.value) {
      state.productId = String(c.value);
    } else if (c.column === 'product_team_ids' && c.action === 'in' && c.value) {
      state.productTeamIds = Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
    }
  }

  return state;
}

/**
 * Convierte el estado local a condiciones para la API (FilterConditionOutput).
 */
function convertToConditions(state: LocationsFiltersState): FilterConditionOutput[] {
  const conditions: FilterConditionOutput[] = [];

  if (state.locationType) {
    conditions.push({
      column: 'locationType',
      action: 'equals',
      value: state.locationType,
    });
  }
  if (state.productId) {
    conditions.push({
      column: 'productName',
      action: 'equals',
      value: state.productId,
    });
  }
  if (state.productTeamIds.length > 0) {
    conditions.push({
      column: 'product_team_ids',
      action: 'in',
      value: state.productTeamIds,
    });
  }

  return conditions;
}

export interface UseLocationsFiltersParams {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  hasTeamData?: boolean;
}

/**
 * Hook para gestionar el estado de filtros del módulo Ubicaciones.
 * Sincroniza con URL vía initialConditions y notifica cambios con onChange.
 */
export const useLocationsFilters = ({
  initialConditions = [],
  onChange,
  hasTeamData = false,
}: UseLocationsFiltersParams) => {
  const [filters, setFilters] = useState<LocationsFiltersState>(() => parseConditions(initialConditions));

  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const teamsAdapter = useMemo(() => new TeamsApiAdapter(httpClient), [httpClient]);

  const { data: clientOptions = [], isLoading: isLoadingClients } = useQuery({
    queryKey: ['locations-filters-clients'],
    queryFn: async () => {
      if (!hasTeamData) return [];
      const teams = await teamsAdapter.getTeamsChild();
      return teams.map((team) => ({ value: team.id.toString(), label: team.name }));
    },
    enabled: hasTeamData,
    staleTime: 5 * 60 * 1000,
  });

  // Sincronizar estado cuando cambien las condiciones externas (ej. desde URL)
  useEffect(() => {
    setFilters(parseConditions(initialConditions));
  }, [initialConditions]);

  const notifyChange = useCallback(
    (next: LocationsFiltersState) => {
      onChange?.(convertToConditions(next));
    },
    [onChange],
  );

  const handleLocationTypeChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, locationType: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleProductChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, productId: value || '' };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleProductTeamChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        const next = { ...prev, productTeamIds: values };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const clearAllFilters = useCallback(() => {
    setFilters(defaultState);
    notifyChange(defaultState);
  }, [notifyChange]);

  return {
    filters,
    handleLocationTypeChange,
    handleProductChange,
    handleProductTeamChange,
    clearAllFilters,
    clientOptions,
    isLoadingClients,
  };
};
